import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Calendar } from '../ui/calendar';

const VIEW_MODES = [
  { id: 'day',   labelKey: 'schedule.viewMode.day',       days: 1 },
  { id: 'three', labelKey: 'schedule.viewMode.threeDays', days: 3 },
];

/**
 * Schedule header: title (clickable to open the date picker), prev/next/
 * today nav, calendar-icon picker button, view-mode toggle.
 *
 * Owns its own popup state and outside-click dismissal. Date navigation
 * is driven by props from the parent (`useDateNav` hook in App).
 */
export function ScheduleHeader({
  titleRange,
  selectedDate,        // JS Date for the Calendar's `selected` prop
  onSelectDate,        // (jsDate) => void; parent updates anchor date
  viewModeId,
  onChangeViewMode,    // (viewModeId, days) => void
  onPrev,
  onNext,
  onToday,
}) {
  const { t } = useTranslation();
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    if (!showCalendar) return;
    const onDocClick = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCalendar]);

  const handleDateSelect = (date) => {
    if (!date) return;
    onSelectDate(date);
    setShowCalendar(false);
  };

  return (
    <header className="wv-header">
      <div className="wv-header-titleblock">
        <h1
          className="wv-header-title wv-header-title--clickable"
          onClick={() => setShowCalendar((s) => !s)}
          title={t('schedule.pickDate')}
        >
          {titleRange}
        </h1>
        {showCalendar && (
          <div className="wv-header-calendar-popup" ref={calendarRef}>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
          </div>
        )}
      </div>
      <div className="wv-header-controls">
        <button className="wv-nav-btn" onClick={onPrev} aria-label={t('buttons.previousDay')}>
          <ChevronLeft size={18} />
        </button>
        <button className="wv-today-btn" onClick={onToday}>{t('buttons.today')}</button>
        <button className="wv-nav-btn" onClick={onNext} aria-label={t('buttons.nextDay')}>
          <ChevronRight size={18} />
        </button>
        <button
          className="wv-nav-btn"
          onClick={() => setShowCalendar((s) => !s)}
          aria-label={t('schedule.pickDate')}
          title={t('schedule.pickDate')}
        >
          <CalendarDays size={18} />
        </button>
        <div className="wv-view-toggle">
          {VIEW_MODES.map((v) => (
            <button
              key={v.id}
              className={`wv-view-toggle-btn${v.id === viewModeId ? ' wv-view-toggle-btn--active' : ''}`}
              onClick={() => onChangeViewMode(v.id, v.days)}
            >
              {t(v.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
